/**
 * One-way ANOVA, with the checks and follow-ups a results section needs.
 *
 * - The classic F test (`scipy.stats.f_oneway`), with η² = SS_B / SS_T and
 *   ω² = (SS_B − df_B · MS_W) / (SS_T + MS_W).
 * - Levene's test in the Brown–Forsythe form: an ANOVA on |x − group median|
 *   (`scipy.stats.levene(center="median")`, R's `car::leveneTest` default). The median
 *   version keeps its error rate under skewed data, which the mean version does not.
 * - Welch's ANOVA (Welch 1951; R's `oneway.test(var.equal = FALSE)`), which does not
 *   assume equal variances. With two groups it reduces to Welch's t-test: F = t².
 * - Pairwise Welch t-tests with a Bonferroni adjustment, as a simple post hoc. Tukey's
 *   HSD is not offered: it needs the studentized range distribution, which this site's
 *   statistics library does not implement.
 */

import { mean, median, variance } from "@/lib/stats/descriptive";
import { fUpper, studentTTwoTailed } from "@/lib/stats/distributions";

export interface GroupStats {
  n: number;
  mean: number;
  sd: number;
  variance: number;
  median: number;
}

export interface FTest {
  f: number;
  df1: number;
  df2: number;
  p: number;
}

export interface OneWayAnova extends FTest {
  groups: GroupStats[];
  grandMean: number;
  n: number;
  ssBetween: number;
  ssWithin: number;
  ssTotal: number;
  msBetween: number;
  msWithin: number;
  etaSquared: number;
  omegaSquared: number;
}

export interface PairwiseComparison {
  first: number;
  second: number;
  difference: number;
  t: number;
  df: number;
  p: number;
  /** min(1, p × number of comparisons). */
  pAdjusted: number;
}

export type AnovaFailure = "too-few-groups" | "too-few-values" | "no-variance";

export type AnovaOutcome =
  | { ok: true; result: OneWayAnova }
  | { ok: false; reason: AnovaFailure };

export function groupStats(values: readonly number[]): GroupStats {
  const v = variance(values);
  return {
    n: values.length,
    mean: mean(values),
    sd: Math.sqrt(v),
    variance: v,
    median: median(values),
  };
}

/** The bare F test, shared by the ANOVA and by Levene's test. */
function fTest(groups: readonly (readonly number[])[]): {
  test: FTest;
  ssBetween: number;
  ssWithin: number;
  grandMean: number;
  n: number;
} {
  const all = groups.flat();
  const n = all.length;
  const grandMean = mean(all);
  let ssBetween = 0;
  let ssWithin = 0;
  for (const group of groups) {
    const m = mean(group);
    ssBetween += group.length * (m - grandMean) ** 2;
    for (const value of group) {
      ssWithin += (value - m) ** 2;
    }
  }
  const df1 = groups.length - 1;
  const df2 = n - groups.length;
  const f = ssBetween / df1 / (ssWithin / df2);
  return {
    test: { f, df1, df2, p: fUpper(f, df1, df2) },
    ssBetween,
    ssWithin,
    grandMean,
    n,
  };
}

export function oneWayAnova(
  groups: readonly (readonly number[])[]
): AnovaOutcome {
  if (groups.length < 2) {
    return { ok: false, reason: "too-few-groups" };
  }
  if (groups.some((group) => group.length < 2)) {
    return { ok: false, reason: "too-few-values" };
  }
  const { test, ssBetween, ssWithin, grandMean, n } = fTest(groups);
  if (ssWithin === 0) {
    return { ok: false, reason: "no-variance" };
  }
  const ssTotal = ssBetween + ssWithin;
  const msBetween = ssBetween / test.df1;
  const msWithin = ssWithin / test.df2;
  return {
    ok: true,
    result: {
      ...test,
      groups: groups.map(groupStats),
      grandMean,
      n,
      ssBetween,
      ssWithin,
      ssTotal,
      msBetween,
      msWithin,
      etaSquared: ssBetween / ssTotal,
      omegaSquared: (ssBetween - test.df1 * msWithin) / (ssTotal + msWithin),
    },
  };
}

/** Levene's test, Brown–Forsythe (median-centred) form. Null when every deviation is 0. */
export function leveneMedian(
  groups: readonly (readonly number[])[]
): FTest | null {
  const deviations = groups.map((group) => {
    const centre = median(group);
    return group.map((value) => Math.abs(value - centre));
  });
  const { test, ssWithin, ssBetween } = fTest(deviations);
  if (ssWithin === 0) {
    return ssBetween === 0
      ? null
      : { ...test, f: Number.POSITIVE_INFINITY, p: 0 };
  }
  return test;
}

/** Welch's heteroscedastic ANOVA. Null when a group has zero variance. */
export function welchAnova(
  groups: readonly (readonly number[])[]
): FTest | null {
  const stats = groups.map(groupStats);
  if (stats.some((group) => group.variance === 0)) {
    return null;
  }
  const k = stats.length;
  const weights = stats.map((group) => group.n / group.variance);
  const weightSum = weights.reduce((acc, w) => acc + w, 0);
  const weightedMean =
    stats.reduce((acc, group, i) => acc + weights[i] * group.mean, 0) /
    weightSum;
  let spread = 0;
  let lambda = 0;
  for (const [i, group] of stats.entries()) {
    spread += weights[i] * (group.mean - weightedMean) ** 2;
    lambda += (1 - weights[i] / weightSum) ** 2 / (group.n - 1);
  }
  const numerator = spread / (k - 1);
  const denominator = 1 + ((2 * (k - 2)) / (k * k - 1)) * lambda;
  const f = numerator / denominator;
  const df1 = k - 1;
  const df2 = (k * k - 1) / (3 * lambda);
  return { f, df1, df2, p: fUpper(f, df1, df2) };
}

/** Every pair of groups, by Welch's t-test, Bonferroni-adjusted. */
export function pairwiseWelch(
  groups: readonly (readonly number[])[]
): PairwiseComparison[] {
  const stats = groups.map(groupStats);
  const comparisons: PairwiseComparison[] = [];
  const m = (groups.length * (groups.length - 1)) / 2;
  for (let i = 0; i < stats.length; i++) {
    for (let j = i + 1; j < stats.length; j++) {
      const a = stats[i];
      const b = stats[j];
      const va = a.variance / a.n;
      const vb = b.variance / b.n;
      const difference = a.mean - b.mean;
      const se = Math.sqrt(va + vb);
      const df = (va + vb) ** 2 / (va ** 2 / (a.n - 1) + vb ** 2 / (b.n - 1));
      const t = se === 0 ? Number.NaN : difference / se;
      const p = Number.isNaN(t) ? Number.NaN : studentTTwoTailed(t, df);
      comparisons.push({
        first: i,
        second: j,
        difference,
        t,
        df,
        p,
        pAdjusted: Math.min(1, p * m),
      });
    }
  }
  return comparisons;
}
